import { useState } from "react"
import { useDispatch } from "react-redux"
import { useForm } from "react-hook-form"
import { z } from "zod/v4"
import { toast } from "sonner"

import * as api from "@/api/cogeser"
import { login } from "@/context/slices/authSlice"
import type { IUser } from "@/context/slices/authSlice"
import { APINotOkError } from "@/api/cogeser"
import cogeserLogo from "@/assets/cogeser-logo.svg"

// ----------------------------------------------------------------------------

const signInSchema = z.object({
  email: z.email("Inserisci un'email valida"),
  password: z.string().min(1, "La password è obbligatoria"),
})

type SignInFormData = z.infer<typeof signInSchema>

// ----------------------------------------------------------------------------

export default function SignIn() {
  // Hooks
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignInFormData>()

  // Component functions
  async function onSubmit(data: SignInFormData) {
    setIsLoading(true)
    try {
      const parsed = signInSchema.parse(data)
      const response = (await api.signIn(parsed.email, parsed.password)) as {
        token: string
        user: IUser
      }
      dispatch(login(response))
      toast.success("Accesso effettuato")
    } catch (error) {
      if (error instanceof APINotOkError) {
        toast.error(error.message || "Credenziali non valide")
      } else {
        toast.error("Errore di connessione al server")
      }
    } finally {
      setIsLoading(false)
    }
  }

  // Component local variables

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden px-4">
      {/* Background gradient + pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#006838] via-[#3a9c4d] to-[#228ccb]" />
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      {/* Login card */}
      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="mb-8 flex flex-col items-center">
          <img src={cogeserLogo} alt="Cogeser" className="h-20 drop-shadow-lg" />
          <p className="mt-3 text-sm font-medium text-white/80">Pannello di gestione</p>
        </div>

        {/* Card */}
        <div className="rounded-2xl bg-white/95 p-8 shadow-2xl backdrop-blur-sm">
          <h2 className="mb-6 text-center text-lg font-semibold text-gray-900">Accedi al backoffice</h2>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {/* Email */}
            <div className="space-y-1.5">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="admin@cogeser.it"
                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm outline-none transition-all focus:border-primary focus:ring-2 focus:ring-primary/20"
                {...register("email", { required: true })}
              />
              {errors.email && (
                <p className="text-xs text-red-500">{errors.email.message}</p>
              )}
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                placeholder="••••••••"
                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm outline-none transition-all focus:border-primary focus:ring-2 focus:ring-primary/20"
                {...register("password", { required: true })}
              />
              {errors.password && (
                <p className="text-xs text-red-500">{errors.password.message}</p>
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full rounded-lg bg-gradient-to-r from-[#006838] to-[#3a9c4d] px-4 py-3 text-sm font-semibold text-white shadow-md transition-all hover:shadow-lg hover:brightness-110 disabled:opacity-50"
            >
              {isLoading ? "Accesso in corso..." : "Accedi"}
            </button>
          </form>
        </div>

        <p className="mt-6 text-center text-xs text-white/50">
          © {new Date().getFullYear()} Cogeser Energia
        </p>
      </div>
    </div>
  )
}
